import React from 'react';
import { Box, Typography } from '@mui/material';
import type {
  NurturingSegment,
  NurturingStep,
  StaticStepHoverData,
} from 'app/pages/NurturingJourneySettingsComponent/types';
import { NurturingStagePanel } from './NurturingStagePanel';

export interface NurturingSegmentContentProps {
  segment: NurturingSegment;
  segmentIndex: number;
  recordType: 'unsold' | 'sold';
  unsoldStageTab: number;
  soldStageTab: number;
  unsoldStageKeys: readonly string[];
  soldStageKeys: readonly string[];
  onReorderSteps: (
    recordType: 'unsold' | 'sold',
    stageKey: string,
    oldIndex: number,
    newIndex: number,
  ) => void;
  onUpdateStep: (
    recordType: 'unsold' | 'sold',
    stageKey: string,
    stepIndex: number,
    patch: Partial<NurturingStep>,
  ) => void;
  onUpdateMaxRepeat: (
    recordType: 'unsold' | 'sold',
    stageKey: string,
    value: number | null,
  ) => void;
  onAddStep: (
    recordType: 'unsold' | 'sold',
    stageKey: string,
    step: NurturingStep,
  ) => void;
  onDeleteStep: (
    recordType: 'unsold' | 'sold',
    stageKey: string,
    stepIndex: number,
  ) => void;
  getStaticStepHoverData?: (
    steps: NurturingStep[],
    staticStepIndex: number,
  ) => StaticStepHoverData;
  onStepZeroReorderBlocked?: () => void;
  onImportStepsFromAllSegment?: () => void;
  disableImportStepsFromAllSegment?: boolean;
  disabled?: boolean;
}

function TabPanel({
  children,
  value,
  index,
}: {
  children: React.ReactNode;
  value: number;
  index: number;
}) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export function NurturingSegmentContent({
  segment,
  segmentIndex,
  recordType,
  unsoldStageTab,
  soldStageTab,
  unsoldStageKeys,
  soldStageKeys,
  onReorderSteps,
  onUpdateStep,
  onUpdateMaxRepeat,
  onAddStep,
  onDeleteStep,
  getStaticStepHoverData,
  onStepZeroReorderBlocked,
  onImportStepsFromAllSegment,
  disableImportStepsFromAllSegment,
  disabled,
}: NurturingSegmentContentProps) {
  const unsoldStages = segment.nurturing_journey_config.record_types.unsold?.nurturing_stages ?? {};
  const soldStages = segment.nurturing_journey_config.record_types.sold?.nurturing_stages ?? {};

  return (
    <Box sx={{ mt: 0.5 }}>
      {recordType === 'unsold' && (
        <>
          {unsoldStageKeys.map((stageKey, i) => (
            <TabPanel key={stageKey} value={unsoldStageTab} index={i}>
              <NurturingStagePanel
                stageKey={stageKey}
                stageLabel={stageKey}
                stage={unsoldStages[stageKey]}
                segmentIndex={segmentIndex}
                recordType="unsold"
                onReorder={(oldIdx, newIdx) =>
                  onReorderSteps('unsold', stageKey, oldIdx, newIdx)
                }
                onUpdateStep={(stepIdx, patch) =>
                  onUpdateStep('unsold', stageKey, stepIdx, patch)
                }
                onUpdateMaxRepeat={v => onUpdateMaxRepeat('unsold', stageKey, v)}
                onAddStep={step => onAddStep('unsold', stageKey, step)}
                onDeleteStep={stepIdx => onDeleteStep('unsold', stageKey, stepIdx)}
                getStaticStepHoverData={getStaticStepHoverData}
                onStepZeroReorderBlocked={onStepZeroReorderBlocked}
                onImportStepsFromAllSegment={onImportStepsFromAllSegment}
                disableImportStepsFromAllSegment={disableImportStepsFromAllSegment}
                disabled={disabled}
              />
            </TabPanel>
          ))}
          {unsoldStageKeys.length === 0 && (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No unsold stages configured.
            </Typography>
          )}
        </>
      )}

      {recordType === 'sold' && (
        <>
          {soldStageKeys.map((stageKey, i) => (
            <TabPanel key={stageKey} value={soldStageTab} index={i}>
              <NurturingStagePanel
                stageKey={stageKey}
                stageLabel={stageKey}
                stage={soldStages[stageKey]}
                segmentIndex={segmentIndex}
                recordType="sold"
                onReorder={(oldIdx, newIdx) =>
                  onReorderSteps('sold', stageKey, oldIdx, newIdx)
                }
                onUpdateStep={(stepIdx, patch) =>
                  onUpdateStep('sold', stageKey, stepIdx, patch)
                }
                onUpdateMaxRepeat={v => onUpdateMaxRepeat('sold', stageKey, v)}
                onAddStep={step => onAddStep('sold', stageKey, step)}
                onDeleteStep={stepIdx => onDeleteStep('sold', stageKey, stepIdx)}
                getStaticStepHoverData={getStaticStepHoverData}
                onStepZeroReorderBlocked={onStepZeroReorderBlocked}
                onImportStepsFromAllSegment={onImportStepsFromAllSegment}
                disableImportStepsFromAllSegment={disableImportStepsFromAllSegment}
                disabled={disabled}
              />
            </TabPanel>
          ))}
          {soldStageKeys.length === 0 && (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No sold stages configured.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}
